import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  useMediaQuery,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { FullScreenHandle } from 'react-full-screen';
import { Instance, Size } from '@nutrient-sdk/viewer';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { ContentProcessingStatus } from '../../../../../enums/ContentProcessingLogs';
import { Events, Properties } from '../../../../../enums/Analytics';
import { getAspectRatioHeight } from '../../../../../lib/Media';
import { getDocumentLabel } from '../../../../../Services/Content/Content';
import { isCustomDomain } from './helper';
import { PDFViewerProps } from '../PDFViewerProps';
import {
  PSPDFKIT_ASSETS_URL,
  PSPDFKIT_VIEWER_STYLESHEET,
} from '../../../../../config/assets';
import { useConversionStatusPolling } from './useConversionPollingStatus';
import { useI18n } from '../../../../../lib/hook/useI18n';
import Analytics from '../../../../../tools/Analytics';
import DocumentActions from '../../../Actions/DocumentActions';
import OrganizationsStore from '../../../../../stores/observables/OrganizationsStore';
import PDFError from '../../PDFError';
import PDFLoader from '../../PDFLoader';
import PSPDFKit from '../../../../../config/PSPDFKit';
import useArrowKeys from '../../../../../lib/hook/useArrowKeys';
import usePDFViewerStyles from '../../usePDFViewerStyles';

/**
 * The average height of documents in our database is 708, so we round it up
 * to 720. This is necessary because there could be documents with an empty
 * height (e.g.: while they're being processed), but PSPDFKit requires the
 * container element to have a non-empty height.
 */
const DEFAULT_HEIGHT = 720;
const FIRST_PAGE = 1;

/**
 * For mobile devices, PDFs outside the viewport will be loaded if they've been
 * inside the screen for at least 1 second and unloaded after 3 seconds
 * off the screen, to avoid performance issues if the room contains lots
 * of PDFs rendered at the same time.
 */
const PDF_INSIDE_VIEWPORT_TIMEOUT = 1000;
const PDF_OUTSIDE_VIEWPORT_TIMEOUT = 3000;

/**
 * The minimum height that a document must have in order to display the loader
 * with its dimensions instead of a fixed height, to avoid much screen jumping
 * if the document is smaller/taller than the loader's height.
 */
const MIN_HEIGHT = 500;

const useStyles = makeStyles((theme: Theme) => ({
  fullHeight: {
    height: '100%',
  },
  fullScreen: {
    height: `calc(100% - ${theme.spacing(8)})`,
  },
}));

interface Props extends PDFViewerProps {
  fullScreenHandle: FullScreenHandle;
}

const PSPDFKitInternalViewer: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const pdfViewerClasses = usePDFViewerStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { t } = useI18n();

  const {
    contentId,
    contentProcessingLog,
    cover,
    darkTheme = false,
    disableDownloadButton = false,
    document,
    downloadable = false,
    enableArrows = false,
    enableViewportDetection = false,
    fullScreenHandle,
    initialPage = FIRST_PAGE,
    onLoad,
    onLoadError,
    onDownload,
    onPageChange,
    previewDialog,
    theaterMode = false,
    trackingProperties,
    userContactId,
    width,
  } = props;

  const {
    filesize,
    height: docHeight,
    mime,
    pages,
    pdfUrl,
    width: docWidth,
  } = document;

  const { conversionStatusLoading, conversionStatus, errorConversion } =
    useConversionStatusPolling({
      contentId,
      contentProcessingLog,
      userContactId,
    });

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [pageHeight, setPageHeight] = useState<number>();
  const [pageWidth, setPageWidth] = useState<number>();

  const [maxZoomLevel, setMaxZoomLevel] = useState<number>();
  const [minZoomLevel, setMinZoomLevel] = useState<number>();
  const [zoom, setZoom] = useState(1);

  const [containerReady, setContainerReady] = useState(0);
  const [isInViewport, setIsInViewport] = useState(!enableViewportDetection);

  const isMobileRef = useRef(false);
  const initialized = useRef(false);
  const loaded = useRef(false);
  const pdfContainer = useRef<HTMLDivElement | null>(null);
  const pdfInstance = useRef<Instance>();
  const skipZoomReset = useRef(false);
  /**
   * Ref to store the zoomMode that PSPDFKit has to reset to whenever a page
   * is zoomed with gestures. A `useRef` hook is needed because the listener of
   * `onPageZoomed` has an outdated `isFullScreen` value since the
   * closure is defined for the moment the `pdfInstance` is assigned.
   * @note using <any> because TS doesn't like using PSPDFKit.ZoomMode
   */
  const zoomMode = useRef<any>(PSPDFKit.ZoomMode.FIT_TO_WIDTH);

  /**
   * For rooms viewed in mobile devices that have more than 5 PDFs, the
   * loader can be restarted, as the PDFs not in the viewport will be
   * unloaded. So allowing to restart the loader allows for a better
   * experience, as users will see the familiar loading state instead of
   * the one from PSPDFKit
   */
  const allowLoaderRestart = enableViewportDetection && isMobile;

  const isFullScreen = fullScreenHandle.active;
  const isDownloadable =
    downloadable && (!disableDownloadButton || isFullScreen);

  /**
   * This hook is necessary to listen to the viewer's page changes, so
   * we can notify this wrapper's parents that a page has changed, for
   * example: the Document Tracker. This is necessary to capture the page changes
   * when the user explicitly clicks on the navigation arrows or if the PDF
   * has links to its pages (like an index page).
   *
   * The event is added/removed every time the page changes, because if
   * these events are added when the ref is assigned in `assignPDFContainerRef`,
   * for some reason, the parent's `onPageChange` method will capture the
   * closure for the exact moment the ref is assigned. For the specific case
   * of the document tracker, it means the intersection observer state will
   * always stay as if it wasn't within the viewport (as it was it's original state).
   *
   * TLDR; the onPageChanged must be re-added every time the page changes
   */
  useEffect(() => {
    const event = 'viewState.currentPageIndex.change';
    const pdf = pdfInstance.current;

    pdf?.addEventListener(event, onPageChanged);
    return () => pdf?.removeEventListener(event, onPageChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfInstance.current, currentPage]);

  /**
   * Resetting the zoomMode to FIT_TO_VIEWPORT when the fullscreen is active
   * and keeping this reference so the listener for `onPageZoomed` doesn't reset
   * it to the dimensions the document had before entering fullscreen
   */
  useEffect(() => {
    if (isFullScreen) {
      zoomMode.current = PSPDFKit.ZoomMode.FIT_TO_VIEWPORT;

      pdfInstance.current?.setViewState((viewState) =>
        viewState.set('zoom', zoomMode.current)
      );
    }

    return () => {
      zoomMode.current = PSPDFKit.ZoomMode.FIT_TO_WIDTH;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullScreen]);

  /**
   * We need this useEffect to update the ref value, since this is used
   * by `onPageZoomed`, but it will always access a non updated version
   * of the variable, because we can't add `isMobile` as a dependency on
   * the `assignPDFContainerRef` because that would mount and initialize
   * listeners and other things more than once, which would cause a
   * PSPDFKit error
   */
  useEffect(() => {
    isMobileRef.current = isMobile;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  /**
   * Effect to load and initialize PSPDFKit when all the conditions for it
   * are met. This is necessary because if the viewer needs to be rendered
   * when it enters the viewport, then the load function must be called
   * only until then.
   *
   * However, this cannot be done in the `assignPDFContainerRef` callback,
   * because the ref to the PDF container's Node needs to always be rendered,
   * in order to call PSPDFKit load function. Therefore, we use the combination
   * of last time the container's ref was updated and whether or not the
   * container is currently in the viewport.
   *
   * If the viewport detection is off, the flag will be `true` and the loading
   * will start as soon as the container's Node is available.
   *
   * @warning DO NOT CHANGE THIS without testing the following:
   *  - Having more than 5 PDFs in a room and view it in a mobile device
   *  - Having more than 10 PDFs in a room and view it in a desktop device
   *  - Tracking for all PDF contents works properly when the PDF is within viewport
   *  - No PSPDFKit errors occur when PDFs enter and exit the viewport
   */
  useEffect(() => {
    /**
     * Using a timeout in case the container enters the viewport AND it has
     * been rendered and is available at the same time. This avoids double
     * loading PSPDFKit, as that would throw an error.
     */
    const timeout = setTimeout(
      () => {
        const container = pdfContainer.current;
        const isNotInitialized = !initialized.current;

        // all the conditions are met to load PSPDFKit
        if (isNotInitialized && isInViewport && containerReady && container) {
          initialized.current = true;
          loadPSPDFKit(container);
        }
      },
      allowLoaderRestart ? PDF_INSIDE_VIEWPORT_TIMEOUT : 0
    );

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerReady, isInViewport]);

  /**
   * Effect for when there are more than 5 PDFs in a mobile device.
   * For performance purposes, PDFs that are not in the viewport need to be
   * unloaded. For any other case, this must not happen, to avoid worsen the
   * user experience, as they will see the loader when they scroll up or down
   * to see other PDFs
   */
  useEffect(() => {
    if (isInViewport || !allowLoaderRestart) {
      return;
    }

    const timeout = setTimeout(() => {
      if (pdfInstance.current) {
        setLoading(true);
        PSPDFKit.unload(pdfInstance.current);
        initialized.current = false;
      }
    }, PDF_OUTSIDE_VIEWPORT_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInViewport]);

  useEffect(() => {
    if (conversionStatus === ContentProcessingStatus.Processing) {
      const trackProperties = {
        ...trackingProperties,
        [Properties.CONTENT_ID]: contentId,
        [Properties.DOCUMENT_TYPE]: getDocumentLabel(mime, 'Unknown'),
        [Properties.FILESIZE]: filesize,
        [Properties.HEIGHT]: docHeight,
        [Properties.NUMBER_PAGES]: pages,
        [Properties.WIDTH]: docWidth,
      };
      Analytics.track(Events.PROCESSING_DOCUMENT_VIEWED, trackProperties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversionStatus]);

  /**
   * The alternative for this is a `useEffect` hook, the problem is that there
   * could be scenarios where the hook runs before the container node is available
   * and there's no way to re-run a `useEffect` by having a `useRef` as a
   * dependency.
   *
   * If by any chance the ref changes, then PSPDFKit is unloaded, to clear the
   * initial container and the initialized flag is reset, to allow PSPDFKit
   * to load the PDF again on the new container.
   */
  const assignPDFContainerRef = useCallback((ref: HTMLDivElement | null) => {
    if (pdfContainer.current) {
      PSPDFKit.unload(pdfContainer.current);
      initialized.current = false;
    }

    pdfContainer.current = ref;
    setContainerReady(Date.now());
  }, []);

  const loadPSPDFKit = async (container: HTMLDivElement) => {
    const org = OrganizationsStore.getCurrentOrganization();

    /**
     * At this point, if it's a custom domain, the org must have a custom
     * license key or a fallback license key, so those could never undefined.
     * Otherwise, the `PDFViewer` (parent component) would not have rendered
     * this component, but we still add a safeguard just in case
     */
    const licenseKey = isCustomDomain()
      ? org?.pdfViewerLicenseKey
      : org?.vendor?.pdfViewerLicenseKey;

    if (!licenseKey) {
      return;
    }

    setLoading(true);

    try {
      pdfInstance.current = await PSPDFKit.load({
        baseUrl: PSPDFKIT_ASSETS_URL,
        container,
        document: pdfUrl,
        licenseKey,
        initialViewState: getViewState(),
        renderPageCallback: onPageLoaded,
        /**
         * This file is available at /public/assets/PSPDFKitViewer.css
         * To update the styles, follow these steps:
         *  1. Apply the changes in said file
         *  2. Upload them to the develop/main branches
         *  3. Deploy them to the respective environments
         */
        styleSheets: [PSPDFKIT_VIEWER_STYLESHEET],
      });

      setupVariables(pdfInstance.current);
      setupStatelessListeners(pdfInstance.current);
    } catch (error) {
      handleLoadError(error);
    }
  };

  const onFirstPage = () => {
    handlePageChange(FIRST_PAGE);
  };

  const onLastPage = () => {
    handlePageChange(totalPages);
  };

  const onNextPage = () => {
    const newPage = currentPage + 1;
    if (newPage <= totalPages) {
      handlePageChange(newPage);
    }
  };

  const onPreviousPage = () => {
    const newPage = currentPage - 1;
    if (newPage > 0) {
      handlePageChange(newPage);
    }
  };

  // arrow listeners can be toggled and always enabled on full screen mode
  useArrowKeys(enableArrows || isFullScreen, {
    onDownKey: onNextPage,
    onLeftKey: onPreviousPage,
    onRightKey: onNextPage,
    onUpKey: onPreviousPage,
  });

  const calculateScale = (): number => {
    if (!(pageHeight && pageWidth && width)) {
      return 1;
    }

    const baseScale = width / pageWidth;
    if (!isFullScreen) {
      return baseScale;
    }

    const windowDimensions =
      fullScreenHandle.node.current?.getBoundingClientRect();

    if (windowDimensions) {
      const scaledPageHeight =
        (windowDimensions.width * pageHeight) / pageWidth;

      const maxHeight = windowDimensions.height - 75;

      return scaledPageHeight <= maxHeight
        ? width / pageWidth
        : maxHeight / pageHeight;
    }

    return baseScale;
  };

  const calculateContainerHeight = (): number | string => {
    if (isFullScreen) {
      return '100%';
    }

    if (!(pageHeight && pageWidth)) {
      return width ? getAspectRatioHeight(width) : DEFAULT_HEIGHT;
    }

    const scale = calculateScale();
    return pageHeight * scale;
  };

  const getViewState = () =>
    new PSPDFKit.ViewState({
      currentPageIndex: initialPage - 1,
      layoutMode: PSPDFKit.LayoutMode.SINGLE,
      readOnly: true,
      scrollMode: PSPDFKit.ScrollMode.DISABLED,
      showToolbar: false,
      // PSPDFKit doesn't expose the ViewportPadding object and TS doesn't like it
      viewportPadding: { horizontal: 0, vertical: 0 } as any,
      zoom: PSPDFKit.ZoomMode.FIT_TO_WIDTH,
      showAnnotations: false,
    });

  const setDocumentPage = (pdfInstance: Instance, pageIndex: number) => {
    pdfInstance.setViewState((currentState) =>
      currentState.set('currentPageIndex', pageIndex)
    );
  };

  const setupVariables = (pdfInstance: Instance) => {
    setTotalPages(pdfInstance.totalPageCount);
    setMaxZoomLevel(pdfInstance.maximumZoomLevel);
    setMinZoomLevel(pdfInstance.minimumZoomLevel);
  };

  const setupStatelessListeners = (pdfInstance: Instance) => {
    pdfInstance.addEventListener('page.press', onPagePressed);
    pdfInstance.addEventListener('viewState.zoom.change', onPageZoomed);
  };

  const handlePageChange = (page: number) => {
    if (pdfInstance.current) {
      const pageIndex = page - 1;
      setDocumentPage(pdfInstance.current, pageIndex);
    }
  };

  const handleLoadError = (error: Error | unknown) => {
    // Safely extract error message to handle different error object types
    // from PSPDFKit/Nutrient SDK which may not be standard Error instances
    let errorMessage = 'Unknown error';
    try {
      if (error instanceof Error) {
        errorMessage = error.message || String(error);
      } else if (error && typeof error === 'object') {
        // Try to extract message from error-like objects
        const errorObj = error as any;
        if (errorObj.message) {
          errorMessage = String(errorObj.message);
        } else if (typeof errorObj.toString === 'function') {
          try {
            errorMessage = errorObj.toString();
          } catch {
            errorMessage = 'Error object could not be converted to string';
          }
        } else {
          errorMessage = 'Non-standard error object';
        }
      } else {
        errorMessage = String(error);
      }
    } catch (e) {
      // Fallback if error extraction fails
      errorMessage = 'Failed to extract error message';
    }

    // Log a safe error message that won't cause issues with error tracking tools
    console.error(`PSPDFKitViewer onLoadError: ${errorMessage}`);

    setLoadError(true);

    if (onLoadError) {
      onLoadError();
    }
  };

  const onPageLoaded = (context: any, index: number, pageSize: Size) => {
    const { height, width } = pageSize;

    setPageHeight(height);
    setPageWidth(width);
    setLoading(false);

    /**
     * When the first page that's loaded is ready, we trigger the loaded callback,
     * as there is no other way to determine that the document was loaded
     * successfully for wrappers like tracking to start working
     */
    if (onLoad && !loaded.current) {
      onLoad(pages || totalPages);
      loaded.current = true;
    }
  };

  /**
   * Since the pdf is displayed within an iframe, if the page loses focus when
   * the user clicks on another tab or something else, we need to restore that
   * focus if the user comes back and click on the iframe. This is not automatic,
   * as the iframe captures the click, so we listen to this event and progammatically
   * focus the parent window to restore the tracking.
   */
  const onPagePressed = () => {
    window.focus();
  };

  /**
   * Regaining focus on the window, as the page could've changed because
   * the user clicked on a PDF page that contains a link to one of its pages.
   * We need to re-focus here as well because otherwise, parents like the
   * Document Tracker wouldn't know they need to track because the window
   * could've lose focus
   */
  const onPageChanged = (index: number) => {
    window.focus();

    const page = index + 1;
    setCurrentPage(page);

    if (onPageChange) {
      onPageChange(page);
    }
  };

  /**
   * Resetting the zoom to its original value after it's been zoomed in/out by
   * gestures. This would ensure that if users accidentally zoom in/out, the
   * doc is restored to its original size, because the gesture behavior
   * from PSPDFKit for zoom is kind of broken. To test this, press the
   * CTRL key and scroll with the trackpad or mouse.
   */
  const onPageZoomed = () => {
    pdfInstance.current?.setViewState((currentState) => {
      const { zoom } = currentState;

      const interactionMode =
        zoom !== zoomMode.current ? PSPDFKit.InteractionMode.PAN : null;

      setZoom(Number.isNaN(+zoom) ? 1 : (zoom as number));
      const newState = currentState.set('interactionMode', interactionMode);

      if (!(skipZoomReset.current || isMobileRef.current)) {
        return newState.set('zoom', zoomMode.current);
      }

      skipZoomReset.current = false;
      return newState;
    });
  };

  const onZoomIn = () => {
    skipZoomReset.current = true;
    pdfInstance.current?.setViewState((currentState) => currentState.zoomIn());
  };

  const onZoomOut = () => {
    skipZoomReset.current = true;
    pdfInstance.current?.setViewState((currentState) => currentState.zoomOut());
  };

  if (loadError || errorConversion) {
    return (
      <Box alignItems="center" display="flex" justifyContent="center" pt={1}>
        <PDFError
          downloadable={downloadable}
          onDownload={onDownload}
          mime={mime}
        />
      </Box>
    );
  }

  if (conversionStatus === ContentProcessingStatus.Processing) {
    const height = docHeight && docHeight > MIN_HEIGHT ? docHeight : undefined;

    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          height: height || theaterMode ? '83vh' : theme.spacing(90),
          overflow: 'hidden',
        })}
      >
        <CircularProgress color="inherit" size={32} sx={{ mb: 1 }} />
        <Typography align="center" gutterBottom variant="h6">
          {t('document.viewer.processing.title')}
        </Typography>
        <Typography align="center" color="textSecondary" variant="body2">
          {t('document.viewer.processing.subtitle')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={classNames(classes.fullHeight, {
        [classes.fullScreen]: isFullScreen,
      })}
    >
      <Box
        className={classNames(classes.fullHeight, {
          [pdfViewerClasses.previewDialog]: previewDialog && !isFullScreen,
        })}
      >
        <PDFLoader
          allowLoaderRestart={allowLoaderRestart}
          classes={{
            root: classes.fullHeight,
            viewer: classes.fullHeight,
          }}
          cover={cover}
          docHeight={docHeight}
          docWidth={docWidth}
          enableViewportDetection={enableViewportDetection}
          key={pdfUrl}
          ready={!loading && !conversionStatusLoading}
          width={width}
        >
          {(inViewport: boolean) => {
            setIsInViewport(inViewport);

            return (
              <Box
                ref={assignPDFContainerRef}
                sx={{
                  height: calculateContainerHeight(),
                  width,
                }}
              />
            );
          }}
        </PDFLoader>
      </Box>

      <Box
        className={classNames({
          [pdfViewerClasses.actionsDarkTheme]: darkTheme,
          [pdfViewerClasses.actionsDarkThemePaddingTop]: darkTheme,
        })}
        position="relative"
      >
        <DocumentActions
          currentPage={currentPage}
          darkTheme={darkTheme}
          disabled={loading}
          downloadable={isDownloadable}
          fullScreenHandle={fullScreenHandle}
          maxZoom={maxZoomLevel}
          minZoom={minZoomLevel}
          onDownload={onDownload}
          onFirstPageClick={onFirstPage}
          onLastPageClick={onLastPage}
          onNextPageClick={onNextPage}
          onPrevPageClick={onPreviousPage}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          trackingProperties={trackingProperties}
          totalPages={totalPages}
          zoom={zoom}
          zoomable={!isMobile}
        />
      </Box>
    </Box>
  );
};

export default PSPDFKitInternalViewer;
